// src/sentry.ts
import * as Sentry from "@sentry/react";

const env = import.meta.env;
let isInitialized = false;

/* -------------------------- helpers -------------------------- */

function getPathnameFromUrl(u: string | URL) {
  try {
    const url = typeof u === "string" ? new URL(u, window.location.href) : u;
    return url.pathname;
  } catch {
    return typeof u === "string" ? u : "[unparseable-url]";
  }
}

function bestEffortCallsite(): string | undefined {
  // Try to capture the first app frame (helps when multiple subsequent calls fire)
  const err = new Error("__stack_probe__");
  const stack = (err.stack || "").split("\n").slice(1);
  const appFrame = stack.find(
    (l) =>
      !/\/node_modules\//.test(l) &&
      !/@sentry\//.test(l) &&
      !/monitorSlowApi|sentry\.ts/.test(l)
  );
  return appFrame?.trim();
}

function sanitizeRequestBody(body: unknown): unknown {
  if (!body) return null;

  try {
    let parsed: unknown;
    if (typeof body === "string") {
      parsed = JSON.parse(body);
    } else if (body instanceof FormData) {
      const formObj: Record<string, string> = {};
      body.forEach((value, key) => {
        formObj[key] =
          typeof value === "string" ? "[FormData String]" : "[FormData File]";
      });
      return formObj;
    } else {
      parsed = body;
    }

    // Sanitize sensitive fields
    const sensitiveFields = [
      "password",
      "token",
      "secret",
      "key",
      "auth",
      "credential",
      "ssn",
      "social",
      "credit",
      "card",
      "cvv",
      "pin",
      "otp",
      "email",
      "phone",
      "address",
      "dob",
      "birthdate",
    ];

    const sanitize = (obj: unknown): unknown => {
      if (typeof obj !== "object" || obj === null) return obj;

      if (Array.isArray(obj)) {
        return obj.map((item) => sanitize(item));
      }

      const sanitized: Record<string, unknown> = {};

      for (const [key, value] of Object.entries(
        obj as Record<string, unknown>
      )) {
        const lowerKey = key.toLowerCase();
        const isSensitive = sensitiveFields.some((field) =>
          lowerKey.includes(field)
        );

        if (isSensitive) {
          sanitized[key] = "[REDACTED]";
        } else if (typeof value === "object" && value !== null) {
          sanitized[key] = sanitize(value);
        } else {
          sanitized[key] = value;
        }
      }

      return sanitized;
    };

    return sanitize(parsed);
  } catch {
    return "[Unparseable Body]";
  }
}

function getErrorCategory(status: number): string {
  if (status >= 400 && status < 500) return "client_error";
  if (status >= 500) return "server_error";
  return "unknown_error";
}

function shouldReportApiFailure(status: number, url: string): boolean {
  if (status === 404) return false;

  // 401 = expired / invalid auth token — expected during normal session lifecycle
  if (status === 401) return false;

  if (!status) return false;

  if (url.includes("/health") || url.includes("/ping")) return false;

  return status >= 400;
}

export type AppLogLevel =
  | "trace"
  | "debug"
  | "info"
  | "warn"
  | "error"
  | "fatal";

/**
 * App-wide structured logging sink → Sentry Logs. Logs are queryable and
 * aggregatable by any attribute and are correlated with errors/traces, which is
 * what makes them useful across the whole app (not just device tracing).
 *
 * No-op unless `enableLogs` is set in Sentry.init, and inherits the same
 * environment gating as the rest of Sentry (off on localhost/cipstaging). Always
 * enriched with `route` + `facilityId`, sanitized to primitive attributes
 * (objects are JSON-stringified, null/undefined dropped), and guaranteed never
 * to throw. Prefer the `logger` helper in `util/logger` at call sites — this is
 * the low-level sink it delegates to.
 */
export const reportLog = (
  level: AppLogLevel,
  message: string,
  attributes: Record<string, unknown> = {}
) => {
  try {
    const attrs: Record<string, string | number | boolean> = {
      route: window.location.pathname,
    };
    const facilityId = window.localStorage.getItem("selectedClinic");
    if (facilityId) attrs.facilityId = facilityId;

    for (const [k, v] of Object.entries(attributes)) {
      if (v === null || v === undefined) continue;
      if (
        typeof v === "string" ||
        typeof v === "number" ||
        typeof v === "boolean"
      ) {
        attrs[k] = v;
      } else {
        try {
          attrs[k] = JSON.stringify(v);
        } catch {
          attrs[k] = String(v);
        }
      }
    }

    switch (level) {
      case "trace":
        Sentry.logger.trace(message, attrs);
        break;
      case "debug":
        Sentry.logger.debug(message, attrs);
        break;
      case "warn":
        Sentry.logger.warn(message, attrs);
        break;
      case "error":
        Sentry.logger.error(message, attrs);
        break;
      case "fatal":
        Sentry.logger.fatal(message, attrs);
        break;
      default:
        Sentry.logger.info(message, attrs);
    }
  } catch {
    /* logging must never throw */
  }
};

function attachApiMonitoring(
  fetchImpl: typeof fetch,
  slowThresholdMs = 10_000,
  abortSlow = false
) {
  const wrapped: typeof fetch = async (input, init) => {
    const start = performance.now();
    const initialPath = (() => {
      if (input instanceof Request) return getPathnameFromUrl(input.url);
      if (input instanceof URL) return getPathnameFromUrl(input);
      return getPathnameFromUrl(input as string);
    })();

    // Extract full URL for better context
    const fullUrl = (() => {
      if (input instanceof Request) return input.url;
      if (input instanceof URL) return input.toString();
      if (typeof input === "string") {
        try {
          return new URL(input, window.location.href).toString();
        } catch {
          return input;
        }
      }
      return "[unknown-url]";
    })();

    // track route + callsite at capture time
    const route = window.location.pathname;
    const callsite = bestEffortCallsite();

    // Optional deadline abort
    let timer: number | undefined;
    let controller: AbortController | undefined;
    let finalInit = init;

    if (abortSlow) {
      controller = new AbortController();
      timer = window.setTimeout(
        () => controller?.abort("deadline_exceeded"),
        slowThresholdMs
      );
      finalInit = { ...init, signal: controller.signal };
    }

    const method =
      init?.method ?? (input instanceof Request ? input.method : "GET");

    // Capture request details for error reporting
    const requestBody =
      init?.body || (input instanceof Request ? input.body : null);
    const sanitizedBody = sanitizeRequestBody(requestBody);
    const requestHeaders =
      init?.headers ||
      (input instanceof Request
        ? Object.fromEntries(input.headers.entries())
        : {});

    let status: number | undefined;
    let ok: boolean | undefined;
    let finalPath = initialPath;

    try {
      const res = await fetchImpl(input, finalInit);

      // Use the *final* URL reported by the browser (correct endpoint if redirected or constructed)
      if (res && typeof res.url === "string" && res.url) {
        finalPath = getPathnameFromUrl(res.url);
      }

      status = res.status;
      ok = res.ok;

      // Report API failures (non-404 errors)
      if (shouldReportApiFailure(status, finalPath)) {
        const facilityId =
          window.localStorage.getItem("selectedClinic") ?? "unknown";
        Sentry.setTag("facilityId", facilityId);

        // Try to get response body for error context (but don't consume the stream)
        let errorResponseBody: unknown = null;
        try {
          const clonedResponse = res.clone();
          const text = await clonedResponse.text();
          if (text) {
            try {
              errorResponseBody = JSON.parse(text);
            } catch {
              errorResponseBody = text.substring(0, 500); // Limit size
            }
          }
        } catch {
          // Ignore response body extraction errors
        }

        const errorCategory = getErrorCategory(status);
        const duration = performance.now() - start;

        Sentry.captureException(
          new Error(`API ${errorCategory}: ${status} ${method} ${finalPath}`),
          {
            level: status >= 500 ? "error" : "warning",
            tags: {
              type: "api_failure",
              method,
              status_code: status.toString(),
              url_path: finalPath,
              route,
              error_category: errorCategory,
              is_server_error: (status >= 500).toString(),
              is_client_error: (status >= 400 && status < 500).toString(),
            },
            extra: {
              duration_ms: Math.round(duration),
              full_url: fullUrl,
              initial_path: initialPath,
              callsite,
              request_body: sanitizedBody,
              request_headers: requestHeaders,
              response_body: errorResponseBody,
              user_agent: navigator.userAgent,
              timestamp: new Date().toISOString(),
            },
            fingerprint: ["api_failure", method, finalPath, status.toString()],
          }
        );

        reportLog(status >= 500 ? "error" : "warn", "api_failure", {
          method,
          status,
          url_path: finalPath,
          error_category: errorCategory,
          duration_ms: Math.round(duration),
        });
      }

      return res;
    } catch (err: unknown) {
      const took = performance.now() - start;
      const error = err as Error;
      const isDeadline =
        error?.name === "AbortError" || error?.message === "deadline_exceeded";

      // Handle network errors and timeouts
      if (isDeadline && took >= slowThresholdMs) {
        const facilityId =
          window.localStorage.getItem("selectedClinic") ?? "unknown";
        Sentry.setTag("facilityId", facilityId);

        Sentry.captureException(new Error("API deadline exceeded (>10s)"), {
          level: "error",
          tags: {
            type: "slow_api",
            method,
            url_path: finalPath,
            route,
            deadline: `${slowThresholdMs}ms`,
            aborted: "true",
          },
          extra: {
            duration_ms: Math.round(took),
            callsite,
            initial_path: initialPath,
            full_url: fullUrl,
            request_body: sanitizedBody,
            request_headers: requestHeaders,
          },
          fingerprint: ["slow_api", method, finalPath, "deadline"],
        });

        reportLog("error", "slow_api_deadline", {
          method,
          url_path: finalPath,
          duration_ms: Math.round(took),
          deadline_ms: slowThresholdMs,
        });
      } else if (!isDeadline) {
        // Report network errors, fetch failures, etc.
        const facilityId =
          window.localStorage.getItem("selectedClinic") ?? "unknown";
        Sentry.setTag("facilityId", facilityId);

        Sentry.captureException(error, {
          level: "error",
          tags: {
            type: "api_network_error",
            method,
            url_path: finalPath,
            route,
            error_name: error?.name || "unknown",
          },
          extra: {
            duration_ms: Math.round(took),
            callsite,
            initial_path: initialPath,
            full_url: fullUrl,
            request_body: sanitizedBody,
            request_headers: requestHeaders,
            error_message: error?.message,
            error_stack: error?.stack,
          },
          fingerprint: [
            "api_network_error",
            method,
            finalPath,
            error?.name || "unknown",
          ],
        });

        reportLog("error", "api_network_error", {
          method,
          url_path: finalPath,
          error_name: error?.name,
          error_message: error?.message,
          duration_ms: Math.round(took),
        });
      }

      throw err;
    } finally {
      if (timer) clearTimeout(timer);
      const duration = performance.now() - start;

      // Report slow APIs (successful but slow)
      if (!abortSlow && duration > slowThresholdMs && ok) {
        const facilityId =
          window.localStorage.getItem("selectedClinic") ?? "unknown";
        Sentry.setTag("facilityId", facilityId);

        Sentry.captureMessage("Slow API response (>10s)", {
          level: "warning",
          tags: {
            type: "slow_api",
            method,
            url_path: finalPath,
            route,
            status_code: status?.toString() || "unknown",
          },
          extra: {
            duration_ms: Math.round(duration),
            status,
            ok,
            callsite,
            initial_path: initialPath,
            full_url: fullUrl,
          },
          fingerprint: ["slow_api", method, finalPath],
        });
      }
    }
  };

  (window as typeof globalThis).fetch = wrapped;
}

/* -------------------------- public API -------------------------- */

// Set user context in Sentry
export const setSentryUser = (userId: string, email: string) => {
  Sentry.setUser({ id: userId, email });
};

export const setSentryExtraContext = (facilityId: string) => {
  Sentry.setExtra("facilityId", facilityId);
};

// Manually report API errors (for cases where fetch wrapper doesn't catch them)
export const reportApiError = (
  error: Error,
  context: {
    method?: string;
    url?: string;
    status?: number;
    requestBody?: unknown;
    responseBody?: unknown;
    duration?: number;
  }
) => {
  // 401 is expected during token refresh / session expiry — never report
  if (context.status === 401) return;

  const facilityId = window.localStorage.getItem("selectedClinic") ?? "unknown";
  Sentry.setTag("facilityId", facilityId);

  const errorCategory = context.status
    ? getErrorCategory(context.status)
    : "network_error";
  const urlPath = context.url ? getPathnameFromUrl(context.url) : "unknown";

  // Network errors (CORS, timeout, etc.) should be treated as errors, not warnings
  const level = context.status
    ? context.status >= 500
      ? "error"
      : "warning"
    : "error"; // Network errors are always "error" level

  Sentry.captureException(error, {
    level,
    tags: {
      type: "manual_api_error",
      method: context.method || "unknown",
      status_code: context.status?.toString() || "unknown",
      url_path: urlPath,
      route: window.location.pathname,
      error_category: errorCategory,
      is_network_error: (!context.status).toString(),
      is_cors_error: (
        error.message.includes("CORS") ||
        error.message.includes("Access-Control")
      ).toString(),
    },
    extra: {
      duration_ms: context.duration ? Math.round(context.duration) : undefined,
      full_url: context.url,
      callsite: bestEffortCallsite(),
      request_body: context.requestBody
        ? sanitizeRequestBody(context.requestBody)
        : null,
      response_body: context.responseBody,
      user_email: window.localStorage.getItem("userEmail"),
      facilityId: facilityId,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    },
    fingerprint: [
      "manual_api_error",
      context.method || "unknown",
      urlPath,
      context.status?.toString() || "unknown",
    ],
  });

  reportLog(level === "warning" ? "warn" : "error", "api_error", {
    method: context.method,
    status: context.status,
    url_path: urlPath,
    error_category: errorCategory,
    error_name: error.name,
    error_message: error.message,
    duration_ms: context.duration ? Math.round(context.duration) : undefined,
  });
};

/**
 * Diagnostics for the assigned-device connectivity state machine. Only the
 * clobber-prevention events are reported — these are the smoking gun for the
 * "false disconnected" bug: a soft/failed fetch that we deliberately did NOT
 * let overwrite a known-good device. They are low-volume by nature and sent as
 * `warning` messages (with stable fingerprints) so they can be counted/alerted
 * on in Sentry. Routine outcomes (online/offline reads) are intentionally NOT
 * logged to avoid noise — the reducer already handles them.
 */
export const reportDeviceConnectivity = (
  event: "fetch_failed_preserved" | "fetch_threw_preserved",
  data: Record<string, unknown> = {}
) => {
  const facilityId = window.localStorage.getItem("selectedClinic") ?? "unknown";
  Sentry.captureMessage(`device.connectivity: ${event}`, {
    level: "warning",
    tags: {
      type: "device_connectivity",
      device_event: event,
      route: window.location.pathname,
    },
    extra: {
      ...data,
      facilityId,
      timestamp: new Date().toISOString(),
    },
    fingerprint: ["device_connectivity", event],
  });
};

/**
 * General-purpose device-assignment diagnostics used to trace the intermittent
 * "device shows as unpaired / No device connected yet" reports (e.g. for
 * kaitlyn+3@circadia.health). Covers: assigned-device null↔set transitions,
 * successful fetches that returned no device, and assign/unassign writes. Sent
 * as `info` messages with a per-event fingerprint so Sentry groups them into one
 * issue (with a running count) rather than flooding, while still being
 * filterable by the logged-in user (Sentry user is set to the email at login).
 */
export const reportDeviceTrace = (
  event: string,
  data: Record<string, unknown> = {},
  options: { capture?: boolean; level?: "info" | "warning" } = {}
) => {
  const facilityId = window.localStorage.getItem("selectedClinic") ?? "unknown";
  const level = options.level ?? "info";
  const enriched = {
    ...data,
    facilityId,
    timestamp: new Date().toISOString(),
  };

  // (a) Structured log (Sentry Logs). This is the primary, queryable record —
  // filter/aggregate by any attribute (device_event, source, patientId, online,
  // becameUnpaired…) and correlate with errors/traces. No-op unless `enableLogs`
  // is set in Sentry.init. Attributes must be primitives, so drop null/undefined
  // and coerce anything exotic to a string.
  const attributes: Record<string, string | number | boolean> = {
    device_event: event,
    route: window.location.pathname,
  };
  for (const [k, v] of Object.entries(enriched)) {
    if (v === null || v === undefined) continue;
    attributes[k] =
      typeof v === "string" || typeof v === "number" || typeof v === "boolean"
        ? v
        : String(v);
  }
  if (level === "warning") Sentry.logger.warn(`device.${event}`, attributes);
  else Sentry.logger.info(`device.${event}`, attributes);

  // (b) Always leave a breadcrumb. Breadcrumbs are a rolling buffer attached to
  // the NEXT captured event, so every device issue (and any unrelated Sentry
  // error) carries the recent device timeline — app_open → online/offline →
  // fetch_empty → transition — inline.
  Sentry.addBreadcrumb({
    category: "device",
    type: "info",
    level,
    message: event,
    data: enriched,
  });

  // (c) Only the meaningful events also become their own grouped issue (carrying
  // the breadcrumb trail). Chatty context events (lifecycle/connectivity) are
  // breadcrumb+log only to avoid issue spam; they still ship to Mixpanel + Logs.
  if (options.capture !== false) {
    Sentry.captureMessage(`device.trace: ${event}`, {
      level,
      tags: {
        type: "device_trace",
        device_event: event,
        route: window.location.pathname,
      },
      extra: enriched,
      fingerprint: ["device_trace", event],
    });
  }
};

// Report custom application errors with context
export const reportAppError = (
  error: Error,
  context: {
    component?: string;
    action?: string;
    userId?: string;
    additionalData?: Record<string, unknown>;
  } = {}
) => {
  const facilityId = window.localStorage.getItem("selectedClinic") ?? "unknown";
  Sentry.setTag("facilityId", facilityId);

  Sentry.captureException(error, {
    level: "error",
    tags: {
      type: "app_error",
      component: context.component || "unknown",
      action: context.action || "unknown",
      route: window.location.pathname,
    },
    extra: {
      callsite: bestEffortCallsite(),
      user_id: window.localStorage.getItem("userEmail"),
      additional_data: context.additionalData,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    },
    fingerprint: [
      "app_error",
      context.component || "unknown",
      context.action || "unknown",
    ],
  });

  reportLog("error", "app_error", {
    component: context.component,
    action: context.action,
    error_name: error.name,
    error_message: error.message,
  });
};

export const initializeSentry = () => {
  if (isInitialized) return;

  // NOTE: You previously disabled Sentry on staging. If you want slow-API
  // events from staging, remove "cipstaging.circadia.link" from this check
  // or control it via an env var.
  const enabled =
    window.location.hostname !== "localhost" &&
    window.location.hostname !== "cipstaging.circadia.link";
  // const enabled = true;

  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    enabled,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      // Forward console.warn/console.error to Sentry Logs. Intentionally NOT
      // including "log" — the app emits many debug console.log lines that would
      // flood (and burn quota on) the Logs stream. Add "log" here if you want
      // verbose forwarding.
      Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        maskAttributes: [
          "password",
          "credit-card",
          "social-security",
          "phone",
          "email",
          "name",
          "address",
          "city",
          "state",
          "zip",
          "country",
          "card-number",
          "card-cvv",
          "card-expiry",
          "card-holder-name",
          "card-holder-email",
          "card-holder-phone",
          "card-holder-address",
          "card-holder-city",
          "card-holder-state",
          "card-holder-zip",
          "card-holder-country",
        ],
        beforeAddRecordingEvent: (event) => {
          // exclude console logs
          if ("category" in event && event.category === "console") return null;
          return event;
        },
        beforeErrorSampling: () => true,
      }),
    ],
    replaysSessionSampleRate: 0.1, // 10% of normal sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    enableLogs: true, // structured logs (Sentry.logger.*) — see reportDeviceTrace
    tracesSampleRate: 0.2, // 20% of traces
    profilesSampleRate: 1.0, // 100% of profiles (adjust if too noisy)
    sendDefaultPii: true,
    tracePropagationTargets: [
      "localhost",
      "https://patientapp.circadia.health",
    ],
    environment:
      window.location.hostname === "localhost"
        ? "local"
        : window.location.hostname.includes("staging")
        ? "staging"
        : "production",
  });

  isInitialized = true;

  // Attach comprehensive API monitoring (slow APIs + failures)
  attachApiMonitoring(window.fetch, 10_000, /* abortSlow */ false);
  // If you prefer to cancel slow requests at 10s, flip to true:
  // attachApiMonitoring(window.fetch, 10_000, true);

  // Seed user/facility context from local storage if available
  const userId = window.localStorage.getItem("userEmail");
  const userEmail = window.localStorage.getItem("userEmail");
  if (userId && userEmail) setSentryUser(userId, userEmail);

  const facilityId = window.localStorage.getItem("selectedClinic");
  if (facilityId) setSentryExtraContext(facilityId);
};

// Initialize immediately for apps that import this module once at bootstrap
initializeSentry();

export const SentryErrorBoundary = Sentry.ErrorBoundary;
